import React, { useEffect, useState } from "react";
import {
  Dropdown,
  Button,
  Input,
  DatePicker,
  Select as AntdSelect,
} from "antd";
import { FilterIcon, AddPrimaryIcon, DeleteIcon, CloseIcon } from "../../icons";
import "./style.scss";
import { useMembers } from "../../hooks/useMembers";
import Select from "../select";
import { useDepots } from "../../hooks/useDepots";

interface FilterDropdownUserProps {
  activeTab: string;
  onChange?: (filters: Record<string, any>) => void;
}

interface DynamicFilter {
  column: string | null;
  condition: string | null;
  value: any;
}

const FilterDropdownUser: React.FC<FilterDropdownUserProps> = ({
  onChange,
  activeTab,
}) => {
  const { data: members = [] } = useMembers(1, ""); // fetch all users
  const [filters, setFilters] = useState<DynamicFilter[]>([]);
  const [visible, setVisible] = useState(false);

  // --- Extract options ---
  const uniqueRoleData = Array.from(
    new Map(
      members
        .filter((m) => m.role_name && m.role_id) // ✅ remove null/empty ones
        .map((m) => [m.role_id, { label: m.role_name, value: m.role_id }]) // ✅ unique by role_id
    ).values()
  );

  const roleOptions = uniqueRoleData;

  // Unique Member Type (label = member_type_name, value = member_type)
  const uniqueMemberTypeData = Array.from(
    new Map(
      members
        .filter((m) => m.member_type_name && m.member_type) // ✅ remove null/empty
        .map((m) => [
          m.member_type,
          { label: m.member_type_name, value: m.member_type },
        ]) // ✅ unique by member_type
    ).values()
  );
  const { data: depots } = useDepots();
  let depotOptions =
    depots?.map((d) => ({ label: d.depot_name, value: d.pikitup_depot_id })) ||
    [];
  const memberTypeOptions = uniqueMemberTypeData;

  const statusOptions = [
    { label: "Active", value: "1" },
    { label: "Inactive", value: "0" },
  ];

  // --- Condition Options by column ---
  const conditionOptionsByColumn: Record<
    string,
    { label: string; value: string }[]
  > = {
    Name: [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
      { label: "Contains", value: "contain" },
      { label: "Does not contain", value: "notContain" },
    ],
    Email: [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
      { label: "Contains", value: "contain" },
      { label: "Does not contain", value: "notContain" },
    ],
    "Mobile number": [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
      { label: "Contains", value: "contain" },
      { label: "Does not contain", value: "notContain" },
    ],
    Role: [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
    ],
    "Member type": [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
    ],
    "Depot name": [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
    ],
    Status: [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
    ],
    "Created on": [
      { label: "Is", value: "is" },
      { label: "Is not", value: "isNot" },
    ],
  };

  // --- Build API Params ---
  const buildApiParams = (filters: DynamicFilter[]) => {
    const params: Record<string, any> = {};
    filters.forEach((f) => {
      if (!f.column || !f.condition || !f.value) return;
      const val = Array.isArray(f.value) ? f.value : [f.value];

      const map = {
        Name: "name",
        Email: "email",
        "Mobile number": "mobile",
        Role: "role",
        "Member type": "member_types",
        "Depot name": "depot_name",
        Status: "status",
        "Created on": "created_date_on",
      } as Record<string, string>;

      const key = map[f.column];
      if (!key) return;

      switch (f.condition) {
        case "is":
          params[`${key}_in`] = val;
          break;
        case "isNot":
          params[`${key}_not_in`] = val;
          break;
        case "contain":
          params[`${key}_contain`] = val;
          break;
        case "notContain":
          params[`${key}_not_contain`] = val;
          break;
      }
    });
    return params;
  };

  useEffect(() => {
    const apiParams = buildApiParams(filters);
    onChange?.(apiParams);
  }, [filters]);

  const addFilter = () =>
    setFilters([...filters, { column: null, condition: null, value: "" }]);
  const clearAllFilters = () => setFilters([]);
  const deleteFilter = (index: number) =>
    setFilters(filters.filter((_, i) => i !== index));
  const updateFilter = (index: number, key: keyof DynamicFilter, val: any) => {
    const updated = [...filters];
    updated[index][key] = val;
    setFilters(updated);
  };

  const baseColumns = [
    "Name",
    "Email",
    "Mobile number",
    "Role",
    "Member type",
    "Depot name",
    "Created on",
  ];

  useEffect(() => {
    setFilters([]);
  }, [activeTab]);

  const columnOptions = (
    activeTab === "all" ? [...baseColumns, "Status"] : baseColumns
  ).map((c) => ({ label: c, value: c }));

  const renderValueField = (f: DynamicFilter, idx: number) => {
    if (!f.column || !f.condition) return null;
    switch (f.column) {
      case "Name":
      case "Email":
      case "Mobile number":
        return (
          <Input
            placeholder={`Enter ${f.column}`}
            value={f.value}
            onChange={(e) => updateFilter(idx, "value", e.target.value)}
            className="custom-input-height"
          />
        );
      case "Role":
        if (f.condition === "is" || f.condition === "isNot") {
          return (
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select Role"
              options={roleOptions}
              value={Array.isArray(f.value) ? f.value : []}
              onChange={(val) => updateFilter(idx, "value", val)}
              className="w-full"
            />
          );
        }
        break;
      case "Member type":
        if (f.condition === "is" || f.condition === "isNot") {
          return (
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select Member Type"
              options={memberTypeOptions}
              value={Array.isArray(f.value) ? f.value : []} // ✅ always array, no empty string
              onChange={(val) => updateFilter(idx, "value", val)}
              className="w-full"
            />
          );
        }
        break;
      case "Depot name":
        if (f.condition === "is" || f.condition === "isNot") {
          return (
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select Depot"
              options={depotOptions}
              value={Array.isArray(f.value) ? f.value : []}
              onChange={(val) => updateFilter(idx, "value", val)}
              className="w-full"
            />
          );
        }
        break;
      case "Status":
        return (
          <Select
            placeholder="Select Status"
            options={statusOptions}
            value={f.value || undefined}
            onChange={(val) => updateFilter(idx, "value", val)}
            className="w-full"
          />
        );
      case "Created on":
        return (
          <DatePicker
            onChange={(date) =>
              updateFilter(idx, "value", date ? date.format("DD-MM-YYYY") : "")
            }
            placeholder="Select Date"
            className="w-full filter-date-picker"
          />
        );
      default:
        return null;
    }
  };

  const activeFilterCount = filters.length;

  const menu = (
    <div className="filter-dropdown">
      <div className="header-wrapper flex justify-between items-center">
        <h4 className="filter-title">Filter</h4>
        <div className="flex justify-center items-center">
          {filters.length > 0 && (
            <Button className="transparent clear-btn" onClick={clearAllFilters}>
              Clear all
            </Button>
          )}
          <span className="cursor-pointer" onClick={() => setVisible(false)}>
            <CloseIcon />
          </span>
        </div>
      </div>

      <div className="custom-filters-wrapper">
        {filters.length > 0 && (
          <div className="dynamic-filters-wrapper">
            <span className="title">All filters</span>
            {filters.map((f, idx) => (
              <div
                key={idx}
                className="dynamic-filter flex flex-wrap gap-2 mb-2"
              >
                <AntdSelect
                  className="column-filter w-full"
                  options={columnOptions}
                  value={f.column}
                  onChange={(val) => {
                    updateFilter(idx, "column", val);
                    updateFilter(idx, "condition", null);
                    updateFilter(idx, "value", "");
                  }}
                  placeholder="Select column"
                />

                {f.column && (
                  <AntdSelect
                    className="condition-filter w-full"
                    options={conditionOptionsByColumn[f.column] || []}
                    value={f.condition}
                    onChange={(val) => updateFilter(idx, "condition", val)}
                    placeholder="Condition"
                  />
                )}

                <div className="filter-value"> {renderValueField(f, idx)}</div>

                <Button
                  icon={<DeleteIcon />}
                  className="transparent delete-btn"
                  onClick={() => deleteFilter(idx)}
                />
              </div>
            ))}
          </div>
        )}

        <div className="footer-btn-wrapper flex items-center justify-between">
          <Button
            onClick={addFilter}
            icon={<AddPrimaryIcon />}
            className="transparent add-filter-btn"
          >
            Add Filter
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={["click"]}
      placement="bottomRight"
      arrow={false}
      overlayClassName="filter-dropdown-wrapper"
      onOpenChange={(val) => setVisible(val)}
      open={visible}
    >
      <Button
        icon={<FilterIcon />}
        className={`transparent table-filter-btn ${
          activeFilterCount > 0 ? "active-filter-btn" : ""
        }`}
      >
        {activeFilterCount > 0 && (
          <span className="filter-count">{activeFilterCount}</span>
        )}
        Filter
      </Button>
    </Dropdown>
  );
};

export default FilterDropdownUser;
